import { useMemo } from 'react';
import { Alert, Col, Form, Row, Typography } from 'antd';
import styles from './styles.module.css';
import { useTranslation } from 'react-i18next';

import { ColorPicker as AntdColorPicker } from 'antd';
import type { Color } from 'antd/es/color-picker';
import { readability } from '@ctrl/tinycolor';
const { Text } = Typography;

type ColorPickerProps = {
  color?: string | { hex?: string };
  onChange?: (color: Color) => void;
};

const normalizeColorValue = (value?: string | { hex?: string }) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    const normalized = value.trim();
    return normalized.startsWith('#') ? normalized : `#${normalized}`;
  }

  if (value.hex) {
    return value.hex.startsWith('#') ? value.hex : `#${value.hex}`;
  }

  return '';
};

const getContrastRatio = (hexColor: string) => {
  if (!hexColor) return 0;
  try {
    return readability(hexColor, '#ffffff');
  } catch {
    return 0;
  }
};

export const ColorPicker = ({ color, onChange }: ColorPickerProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const watchedColor = Form.useWatch('color1', form);

  const normalizedInputColor = useMemo(
    () => normalizeColorValue(watchedColor ?? color),
    [watchedColor, color]
  );
  const contrastRatio = useMemo(
    () => getContrastRatio(normalizedInputColor),
    [normalizedInputColor]
  );
  const isAccessibleOnWhite = contrastRatio >= 4.5;
  const previewColor = normalizedInputColor || '#000000';

  return (
    <Row gutter={24}>
      <Col xs={16} md={12} lg={8} xl={6} xxl={4}>
        <Form.Item
          label={<Text strong>{t('fg_color_label')}</Text>}
          name="color1"
          getValueFromEvent={(color: Color) => color.toHexString()}
          getValueProps={(value) => {
            if (typeof value === 'string') {
              return { value: value.startsWith('#') ? value : `#${value}` };
            }
            if (value?.hex) {
              return {
                value: value.hex.startsWith('#') ? value.hex : `#${value.hex}`,
              };
            }
            return { value: normalizeColorValue(color) };
          }}
          rules={[
            {
              validator: (_, value) => {
                const evaluatedColor = normalizeColorValue(
                  value ?? normalizedInputColor
                );
                if (!evaluatedColor) {
                  return Promise.resolve();
                }
                return getContrastRatio(evaluatedColor) >= 4.5
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(t('fgc_graphics_color_contrast_error') || '')
                    );
              },
            },
          ]}
          validateTrigger={['onChange', 'onBlur']}
        >
          <AntdColorPicker onChangeComplete={onChange} />
        </Form.Item>
      </Col>
      <Col xs={4} md={4} lg={4} xl={2} xxl={2} className={styles.centeredCol}>
        <Form.Item label="hidden">
          <div
            className={styles.colorPreview}
            style={{
              backgroundColor: previewColor,
            }}
          ></div>
        </Form.Item>
      </Col>
      <Col xs={24} md={24} lg={8} xl={8} xxl={8} className={styles.centeredCol}>
        <Form.Item label="hidden">
          <div className={styles.tips}>
            {t('fgc_graphics_color-help')}
            <br />
            {t('fgc_graphics_color-suggestion')}
          </div>
        </Form.Item>
      </Col>
      {!isAccessibleOnWhite && normalizedInputColor ? (
        <Alert
          type="error"
          showIcon
          description={t('fgc_graphics_color_contrast_alert_description')}
          className="w-full max-w-3xl"
        />
      ) : null}
    </Row>
  );
};
